import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { LogOut, Trash2, RefreshCw, Calendar, Plus, Upload, Package } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { BUSINESS, productImage } from "../lib/site";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  pending: "bg-white/5 text-[#8A8A8A] border-white/15",
  confirmed: "bg-[#C9A24B] text-black border-[#C9A24B]",
  completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  cancelled: "bg-red-500/10 text-red-300 border-red-500/30",
};

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await onLogin(email, password);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative">
      <div className="grain-overlay" />
      <motion.div
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
        className="w-full max-w-sm bg-[#141414] border border-[#C9A24B]/30 rounded-sm p-10"
      >
        <div className="flex items-center gap-3 mb-8">
          <img src={BUSINESS.logo} alt="El Japonais" className="h-10 w-10 rounded-full object-cover ring-1 ring-[#C9A24B]/50" />
          <span className="font-serif-display text-xl gold-gradient-text">El Japonais</span>
        </div>
        <p className="text-xs uppercase tracking-[0.3em] text-[#C9A24B] mb-2">Admin</p>
        <h1 className="font-serif-display text-3xl font-light text-[#FAFAFA] mb-8">Dashboard login</h1>
        <form onSubmit={submit} className="space-y-5">
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.15em] text-[#8A8A8A]">Email</Label>
            <Input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[#0A0A0A] border-white/10 focus-visible:ring-[#C9A24B]" />
          </div>
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.15em] text-[#8A8A8A]">Password</Label>
            <Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-[#0A0A0A] border-white/10 focus-visible:ring-[#C9A24B]" />
          </div>
          {error && <p className="text-xs text-red-400" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-submit"
            className="w-full px-6 py-3.5 bg-[#C9A24B] text-black text-xs uppercase tracking-[0.2em] font-semibold rounded-sm hover:bg-[#d9b463] transition-colors duration-300 disabled:opacity-60">
            {loading ? "Signing in…" : "Enter Dashboard"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}

function Dashboard() {
  const { user, logout, token } = useAuth();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, completed: 0, cancelled: 0 });
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const [a, s] = await Promise.all([
        axios.get(`${API}/appointments`, auth),
        axios.get(`${API}/appointments/stats`, auth),
      ]);
      setRows(a.data); setStats(s.data);
    } catch { toast.error("Failed to load bookings"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/appointments/${id}/status`, { status }, auth);
      toast.success(`Marked ${status}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    try { await axios.delete(`${API}/appointments/${id}`, auth); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const cards = [
    { k: "total", label: "Total" }, { k: "pending", label: "Pending" },
    { k: "confirmed", label: "Confirmed" }, { k: "completed", label: "Completed" },
  ];

  return (
    <div className="min-h-screen relative">
      <div className="grain-overlay" />
      <header className="border-b border-white/10 bg-[#141414]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={BUSINESS.logo} alt="El Japonais" className="h-10 w-10 rounded-full object-cover ring-1 ring-[#C9A24B]/50" />
            <div>
              <p className="font-serif-display text-lg gold-gradient-text leading-none">El Japonais</p>
              <p className="text-[11px] text-[#8A8A8A] tracking-wide">Booking dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={load} data-testid="admin-refresh" className="text-[#8A8A8A] hover:text-[#C9A24B] transition-colors"><RefreshCw size={18} /></button>
            <span className="hidden sm:block text-xs text-[#8A8A8A]">{user.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#8A8A8A] hover:text-[#FAFAFA] transition-colors">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
        <Tabs defaultValue="bookings">
          <TabsList className="bg-[#141414] border border-white/10 rounded-sm mb-8">
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="data-[state=active]:bg-[#C9A24B] data-[state=active]:text-black text-xs uppercase tracking-[0.15em]">
              <Calendar size={14} className="mr-2" /> Bookings
            </TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products" className="data-[state=active]:bg-[#C9A24B] data-[state=active]:text-black text-xs uppercase tracking-[0.15em]">
              <Package size={14} className="mr-2" /> Products
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              {cards.map((c) => (
                <div key={c.k} className="bg-[#141414] border border-white/10 rounded-sm p-6" data-testid={`stat-${c.k}`}>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-[#8A8A8A] mb-3">{c.label}</p>
                  <p className="font-serif-display text-4xl text-[#FAFAFA]">{stats[c.k]}</p>
                </div>
              ))}
            </div>

            <div className="bg-[#141414] border border-white/10 rounded-sm overflow-hidden">
              <div className="px-6 py-5 border-b border-white/10 flex items-center gap-3">
                <Calendar size={18} className="text-[#C9A24B]" />
                <h2 className="font-serif-display text-2xl text-[#FAFAFA]">Appointments</h2>
              </div>
              <div className="overflow-x-auto custom-scrollbar">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      {["Client", "Service", "Date", "Time", "Contact", "Status", ""].map((h) => (
                        <TableHead key={h} className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A]">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.length === 0 && (
                      <TableRow className="border-white/10"><TableCell colSpan={7} className="text-center py-16 text-[#8A8A8A]">No bookings yet.</TableCell></TableRow>
                    )}
                    {rows.map((r) => (
                      <TableRow key={r.id} className="border-white/10 hover:bg-white/[0.02]" data-testid={`booking-row-${r.id}`}>
                        <TableCell className="text-[#FAFAFA] font-medium">{r.name}</TableCell>
                        <TableCell className="text-[#8A8A8A]">{r.service}</TableCell>
                        <TableCell className="text-[#8A8A8A]">{r.date}</TableCell>
                        <TableCell className="text-[#8A8A8A]">{r.time}</TableCell>
                        <TableCell className="text-[#8A8A8A] text-xs">{r.phone}{r.email ? <><br />{r.email}</> : null}</TableCell>
                        <TableCell>
                          <Select value={r.status} onValueChange={(v) => changeStatus(r.id, v)}>
                            <SelectTrigger data-testid={`status-select-${r.id}`} className={`h-8 w-32 text-[10px] uppercase tracking-wider rounded-sm border ${STATUS_STYLE[r.status]}`}><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-[#141414] border-white/10 text-[#FAFAFA]">
                              {["pending", "confirmed", "completed", "cancelled"].map((s) => <SelectItem key={s} value={s} className="text-xs uppercase tracking-wider">{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <button onClick={() => remove(r.id)} data-testid={`delete-${r.id}`} className="text-[#8A8A8A] hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="products">
            <ProductsManager token={token} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function ProductsManager({ token }) {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ name: "", price: "", description: "" });
  const [imagePath, setImagePath] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/products`); setProducts(data); }
    catch { toast.error("Failed to load products"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/upload`, fd, { headers: { ...auth.headers } });
      setImagePath(data.image_path);
      setPreview(URL.createObjectURL(file));
      toast.success("Image uploaded");
    } catch { toast.error("Image upload failed"); }
    finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price) { toast.error("Name and price are required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/products`, {
        name: form.name, price: parseFloat(form.price), description: form.description, image_path: imagePath,
      }, auth);
      toast.success("Product added");
      setForm({ name: "", price: "", description: "" });
      setImagePath(null); setPreview(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch { toast.error("Failed to add product"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await axios.delete(`${API}/products/${id}`, auth); toast.success("Product removed"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <form onSubmit={save} className="bg-[#141414] border border-white/10 rounded-sm p-6 space-y-4 h-fit" data-testid="product-form">
        <h3 className="font-serif-display text-2xl text-[#FAFAFA] mb-2">Add product</h3>
        <div className="space-y-2">
          <Label className="text-[11px] uppercase tracking-[0.15em] text-[#8A8A8A]">Name</Label>
          <Input data-testid="product-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="bg-[#0A0A0A] border-white/10 focus-visible:ring-[#C9A24B]" />
        </div>
        <div className="space-y-2">
          <Label className="text-[11px] uppercase tracking-[0.15em] text-[#8A8A8A]">Price (CAD)</Label>
          <Input data-testid="product-price" type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} className="bg-[#0A0A0A] border-white/10 focus-visible:ring-[#C9A24B]" />
        </div>
        <div className="space-y-2">
          <Label className="text-[11px] uppercase tracking-[0.15em] text-[#8A8A8A]">Description</Label>
          <Textarea data-testid="product-description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} className="bg-[#0A0A0A] border-white/10 focus-visible:ring-[#C9A24B] resize-none" />
        </div>
        <div className="space-y-2">
          <Label className="text-[11px] uppercase tracking-[0.15em] text-[#8A8A8A]">Image</Label>
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} data-testid="product-image-input" className="hidden" />
          <button type="button" onClick={() => fileRef.current?.click()} className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-dashed border-white/20 rounded-sm text-xs uppercase tracking-[0.15em] text-[#8A8A8A] hover:border-[#C9A24B] hover:text-[#C9A24B] transition-colors">
            <Upload size={14} /> {uploading ? "Uploading…" : preview ? "Change image" : "Upload image"}
          </button>
          {preview && <img src={preview} alt="preview" className="w-full h-32 object-cover rounded-sm border border-white/10" />}
        </div>
        <button type="submit" disabled={saving || uploading} data-testid="product-save" className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-[#C9A24B] text-black text-xs uppercase tracking-[0.2em] font-semibold rounded-sm hover:bg-[#d9b463] transition-colors duration-300 disabled:opacity-60">
          <Plus size={15} /> {saving ? "Saving…" : "Add product"}
        </button>
      </form>

      <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 h-fit">
        {products.length === 0 && <p className="text-[#8A8A8A] text-sm">No products yet. Add your first product.</p>}
        {products.map((p) => (
          <div key={p.id} className="bg-[#141414] border border-white/10 rounded-sm overflow-hidden flex" data-testid={`admin-product-${p.id}`}>
            <img src={productImage(p)} alt={p.name} className="w-24 h-full object-cover bg-[#0A0A0A]" />
            <div className="p-4 flex-1 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <h4 className="font-serif-display text-lg text-[#FAFAFA] leading-tight">{p.name}</h4>
                <button onClick={() => del(p.id)} data-testid={`product-delete-${p.id}`} className="text-[#8A8A8A] hover:text-red-400 shrink-0"><Trash2 size={15} /></button>
              </div>
              <p className="text-xs text-[#8A8A8A] flex-1 mt-1 line-clamp-2">{p.description}</p>
              <span className="text-[#C9A24B] text-sm mt-2">${p.price.toFixed(2)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, login } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-[#8A8A8A]">Loading…</div>;
  if (!user) return <LoginScreen onLogin={login} />;
  return <Dashboard />;
}
